import React, { useState, useCallback } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Plus, Pencil, Copy, Archive, ArchiveRestore, Trash2, ArrowRight } from 'lucide-react';
import { TimelineEvent } from '../../types/timeline';
import { BuildingSummary } from '../../types/protocols';
import { ReportDraft, ReportDraftFilters } from '../../types/reportDrafts';
import { useReportDrafts } from '../../hooks/reportDrafts';
import { computeSeedItems } from '../../utils/reportDraftView';
import { formatDateTimeForDisplay } from '../../Helpers/DateUtils';
import { isNewTabClick } from '../../hooks/useOpenPath';
import TimelineFilterForm from './TimelineFilterForm';

interface ReportDraftsManagerModalProps {
    show: boolean;
    onHide: () => void;
    allEvents: TimelineEvent[];
    buildingSummaries: Map<string, BuildingSummary>;
}

type ManagerView =
    | { view: 'list' }
    | { view: 'create' }
    | { view: 'rename'; draft: ReportDraft }
    | { view: 'duplicate'; draft: ReportDraft };

/** Default to the current year, or the most recent year that has events —
 *  e.g. January's annual report over last year (review F7). */
const defaultFilters = (allEvents: TimelineEvent[]): ReportDraftFilters => {
    const currentYear = new Date().getFullYear();
    const years = allEvents.map(e => e.date.getFullYear());
    const year = years.includes(currentYear)
        ? currentYear
        : (years.length > 0 ? Math.max(...years) : currentYear);
    return { startYear: year, endYear: year, scope: { type: 'all' } };
};

/**
 * Admin-only drafts manager: list, create (name + filter seed), rename,
 * duplicate, archive and delete report drafts. Opening a draft navigates to
 * the full-page editor (plan D9).
 */
const ReportDraftsManagerModal: React.FC<ReportDraftsManagerModalProps> = ({
    show,
    onHide,
    allEvents,
    buildingSummaries
}) => {
    const navigate = useNavigate();
    const {
        activeDrafts,
        archivedDrafts,
        loading,
        error,
        submitting,
        createDraft,
        renameDraft,
        duplicateDraft,
        setArchived,
        deleteDraft,
        clearError
    } = useReportDrafts();

    const [managerView, setManagerView] = useState<ManagerView>({ view: 'list' });
    const [nameInput, setNameInput] = useState('');
    const [createFilters, setCreateFilters] = useState<ReportDraftFilters>(() => defaultFilters(allEvents));
    const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    const backToList = useCallback(() => {
        setManagerView({ view: 'list' });
        setNameInput('');
        setCreateFilters(defaultFilters(allEvents));
        clearError();
    }, [allEvents, clearError]);

    const handleClose = useCallback(() => {
        backToList();
        setDeleteConfirmKey(null);
        onHide();
    }, [backToList, onHide]);

    const openDraft = useCallback((draftKey: string) => {
        handleClose();
        navigate(`/report-drafts/${draftKey}`);
    }, [handleClose, navigate]);

    const handleCreate = useCallback(async () => {
        try {
            const seedItems = computeSeedItems(allEvents, createFilters);
            const draft = await createDraft(nameInput, createFilters, seedItems);
            openDraft(draft.key);
        } catch {
            // error state is set by the hook
        }
    }, [allEvents, createFilters, nameInput, createDraft, openDraft]);

    const handleRename = useCallback(async () => {
        if (managerView.view !== 'rename') return;
        try {
            await renameDraft(managerView.draft.key, nameInput);
            backToList();
        } catch {
            // error state is set by the hook
        }
    }, [managerView, nameInput, renameDraft, backToList]);

    const handleDuplicate = useCallback(async () => {
        if (managerView.view !== 'duplicate') return;
        try {
            const copy = await duplicateDraft(managerView.draft.key, nameInput);
            openDraft(copy.key);
        } catch {
            // error state is set by the hook
        }
    }, [managerView, nameInput, duplicateDraft, openDraft]);

    const draftRow = (draft: ReportDraft) => (
        <div key={draft.key} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
            <Link
                to={`/report-drafts/${draft.key}`}
                className="flex-grow-1 me-2 text-reset"
                // A plain click leaves the modal; Cmd/Ctrl-click keeps it open behind the new tab.
                onClick={(e) => { if (!isNewTabClick(e)) handleClose(); }}
            >
                <div className="fw-semibold">
                    {draft.name}
                    {draft.archived && <Badge bg="secondary" className="ms-2">Arkiverat</Badge>}
                </div>
                <div className="small text-muted">
                    Ändrat {formatDateTimeForDisplay(draft.updatedAt || draft.createdAt)}
                    {' · '}{Object.keys(draft.items || {}).length} händelser
                </div>
            </Link>
            {deleteConfirmKey === draft.key ? (
                <div className="d-flex gap-2 align-items-center">
                    <span className="small">Radera utkastet?</span>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={submitting}
                        onClick={async () => {
                            try {
                                await deleteDraft(draft.key);
                            } finally {
                                setDeleteConfirmKey(null);
                            }
                        }}
                    >
                        Radera
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setDeleteConfirmKey(null)}>
                        Avbryt
                    </Button>
                </div>
            ) : (
                <div className="d-flex gap-1">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Byt namn"
                        onClick={() => { setManagerView({ view: 'rename', draft }); setNameInput(draft.name); }}
                    >
                        <Pencil size={14} />
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Kopiera"
                        onClick={() => { setManagerView({ view: 'duplicate', draft }); setNameInput(`Kopia av ${draft.name}`); }}
                    >
                        <Copy size={14} />
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        title={draft.archived ? 'Återställ' : 'Arkivera'}
                        disabled={submitting}
                        onClick={() => setArchived(draft.key, !draft.archived)}
                    >
                        {draft.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        title="Radera"
                        onClick={() => setDeleteConfirmKey(draft.key)}
                    >
                        <Trash2 size={14} />
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        title="Öppna"
                        onClick={() => openDraft(draft.key)}
                    >
                        <ArrowRight size={14} />
                    </Button>
                </div>
            )}
        </div>
    );

    const isNameStep = managerView.view === 'rename' || managerView.view === 'duplicate';

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FileText size={20} className="me-2" style={{ verticalAlign: 'text-bottom' }} />
                    {managerView.view === 'create' ? 'Nytt rapportutkast' : 'Rapportutkast'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={clearError} dismissible>
                        {error}
                    </Alert>
                )}

                {managerView.view === 'list' && (
                    <>
                        {loading ? (
                            <div className="text-center py-4"><Spinner animation="border" /></div>
                        ) : (
                            <>
                                {activeDrafts.length === 0 && archivedDrafts.length === 0 && (
                                    <p className="text-muted">
                                        Inga rapportutkast ännu. Skapa ett för att kurera en rapport
                                        över vad som hänt under året.
                                    </p>
                                )}
                                {activeDrafts.map(draftRow)}
                                {archivedDrafts.length > 0 && (
                                    <div className="mt-3">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 mb-2"
                                            onClick={() => setShowArchived(v => !v)}
                                        >
                                            {showArchived ? 'Dölj arkiverade' : `Visa arkiverade (${archivedDrafts.length})`}
                                        </Button>
                                        {showArchived && archivedDrafts.map(draftRow)}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {managerView.view === 'create' && (
                    <>
                        <Form.Group className="mb-4">
                            <Form.Label>Namn på utkastet</Form.Label>
                            <Form.Control
                                type="text"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                placeholder={`T.ex. Årsrapport ${new Date().getFullYear()}`}
                                autoFocus
                            />
                            <Form.Text className="text-muted">
                                Namnet är internt; rapportens titel ändras i utkastet.
                            </Form.Text>
                        </Form.Group>
                        <TimelineFilterForm
                            allEvents={allEvents}
                            buildingSummaries={buildingSummaries}
                            filters={createFilters}
                            onChange={setCreateFilters}
                        />
                    </>
                )}

                {isNameStep && (
                    <Form.Group>
                        <Form.Label>
                            {managerView.view === 'rename' ? 'Nytt namn' : 'Namn på kopian'}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                {managerView.view === 'list' ? (
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            Stäng
                        </Button>
                        <Button variant="primary" onClick={() => setManagerView({ view: 'create' })}>
                            <Plus size={16} className="me-2" />
                            Nytt utkast
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="secondary" onClick={backToList} disabled={submitting}>
                            Tillbaka
                        </Button>
                        {managerView.view === 'create' && (
                            <Button variant="primary" onClick={handleCreate} disabled={submitting || !nameInput.trim()}>
                                {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : <Plus size={16} className="me-2" />}
                                Skapa utkast
                            </Button>
                        )}
                        {managerView.view === 'rename' && (
                            <Button variant="primary" onClick={handleRename} disabled={submitting || !nameInput.trim()}>
                                Spara namn
                            </Button>
                        )}
                        {managerView.view === 'duplicate' && (
                            <Button variant="primary" onClick={handleDuplicate} disabled={submitting || !nameInput.trim()}>
                                Kopiera
                            </Button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ReportDraftsManagerModal;
